/**
 * @fileoverview 단일 채널 정보를 조회합니다.
 * 단, 채널에 종속된 채팅 메시지를 반환하지 않습니다.
 * 채널에 종속된 채팅 메시지가 필요하다면 getMessages 함수를 사용해야 합니다.
 */
import { GetChannelRequest } from "@daangn/idl/rocketchat/api/api";
import { normalize } from "@data-client/normalizr";
import { isNil } from "lodash";
import type { ChatMessage } from "~/rocketchat/chatMessage/chatMessage.entity";
import { sendRocketchatRequestWithPromise } from "~/rocketchat/rocketChatClient";
import type {
  ChannelID,
  NormalizedResult,
  ResourceType,
} from "~/rocketchat/types";
import type { User } from "~/rocketchat/user/user.entity";
import { Channel } from "../channel.entity";

type GetChannelParams = {
  channelId: ChannelID;
};
export async function getChannel(
  { channelId }: GetChannelParams,
  socket: WebSocket,
) {
  const response = await sendRocketchatRequestWithPromise({
    socket,
    chatRequest: {
      name: "GetChannelRequest",
      get_channel: GetChannelRequest.create({
        channel_id: channelId,
      }),
    },
  });

  const channel = response.get_channel?.channel;
  if (isNil(channel)) {
    throw new Error("getChannel: channel is nil");
  }

  const normalizedChannel = normalize<
    typeof Channel,
    {
      Channel: NormalizedResult<Channel>;
      User: NormalizedResult<User>;
      ChatMessage: NormalizedResult<ChatMessage>;
    }
  >(Channel, {
    ...channel,
    unread_count: channel.unread_count ?? 0,
  });

  return {
    channel: normalizedChannel.entities.Channel[channel.id],
    users: normalizedChannel.entities.User,
    lastChatMessage:
      normalizedChannel.entities.ChatMessage?.[channel.last_message_id],
    raw: channel,
  };
}

type GetChannelForceParams = {
  resourceType: ResourceType;
  articleDbId: string;
  sender: User;
  receiver: User;
};
export async function getChannelForce(
  { resourceType, articleDbId, sender, receiver }: GetChannelForceParams,
  socket: WebSocket,
) {
  const response = await sendRocketchatRequestWithPromise({
    socket,
    chatRequest: {
      name: "GetChannelRequest",
      get_channel: GetChannelRequest.create({
        channel_id: "",
        resource_type: resourceType,
        resource_id: articleDbId,
        with_last_message: true,
        force: true,
        sender,
        receiver,
      }),
    },
  });
  const channel = response.get_channel?.channel;
  if (isNil(channel)) {
    throw new Error("getChannel: channel is nil");
  }
  const normalizedChannel = normalize<
    typeof Channel,
    {
      Channel: NormalizedResult<Channel>;
      User: NormalizedResult<User>;
      ChatMessage: NormalizedResult<ChatMessage>;
    }
  >(Channel, channel);

  return {
    channel: normalizedChannel.entities.Channel[channel.id],
    users: normalizedChannel.entities.User,
    raw: channel,
  };
}
